import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Clock, CheckCircle, Award, Users, Shield } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { companyInfo } from '../../data/mock';

const AboutPage = () => {
  const values = [
    {
      icon: Award,
      title: "Expertise",
      description: "Plus de 20 ans d'expérience en vitrerie, serrurerie et menuiserie"
    },
    {
      icon: Clock,
      title: "Réactivité",
      description: "Intervention rapide en 30 minutes, disponible 24h/24 et 7j/7"
    },
    {
      icon: Shield,
      title: "Garantie",
      description: "Travail soigné avec garantie sur toutes nos prestations"
    },
    {
      icon: Users,
      title: "Proximité",
      description: "Artisans locaux au service des habitants du Val-de-Marne"
    }
  ];

  return (
    <div className="py-12 md:py-16">
      <div className="max-w-7xl mx-auto px-4">
        {/* Breadcrumb */}
        <nav className="mb-8 text-sm">
          <ol className="flex items-center gap-2 text-[#666666]">
            <li><Link to="/" className="hover:text-[#B91C1C]">Accueil</Link></li>
            <li>/</li>
            <li className="text-[#262626] font-medium">À propos</li>
          </ol>
        </nav>

        {/* Hero */}
        <div className="text-center mb-16">
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#262626] mb-4">
            À propos d'EMS Express
          </h1>
          <p className="text-lg text-[#666666] max-w-2xl mx-auto">
            Artisan vitrier, serrurier et menuisier depuis plus de 20 ans, 
            au service des particuliers et professionnels de Vitry-sur-Seine et du Val-de-Marne.
          </p>
        </div>

        {/* Story with Logo */}
        <div className="grid lg:grid-cols-2 gap-12 mb-16 items-center">
          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-[#262626] mb-4">
              Notre histoire
            </h2>
            <p className="text-[#666666] mb-4">
              Fondée dans le Val-de-Marne, <strong>EMS Express Miroiterie Serrurerie</strong> est une entreprise 
              familiale spécialisée dans les métiers de la vitrerie, de la serrurerie 
              et de la menuiserie.
            </p>
            <p className="text-[#666666] mb-4">
              Depuis plus de 20 ans, nous mettons notre savoir-faire au service des 
              habitants et professionnels de <strong>Vitry-sur-Seine</strong> et des communes environnantes. 
              Notre expérience nous permet de répondre à toutes vos demandes.
            </p>
            <p className="text-[#666666]">
              Disponibles 24h/24 et 7j/7 pour les urgences, nous intervenons rapidement 
              avec un seul objectif : votre satisfaction.
            </p>
          </div>
          <div className="flex justify-center">
            <div className="relative">
              <div className="absolute -inset-4 bg-[#B91C1C]/10 rounded-3xl blur-xl"></div>
              <img 
                src={companyInfo.logo}
                alt="EMS Express - Vitrier Serrurier"
                className="relative w-72 h-auto object-contain rounded-2xl"
              />
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="bg-[#B91C1C] rounded-2xl p-8 text-white mb-16">
          <h3 className="text-xl font-bold mb-6 text-center">EMS Express en chiffres</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            <div>
              <p className="text-4xl font-bold">20+</p>
              <p className="text-white/80">Années d'expérience</p>
            </div>
            <div>
              <p className="text-4xl font-bold">14</p>
              <p className="text-white/80">Communes couvertes</p>
            </div>
            <div>
              <p className="text-4xl font-bold">30</p>
              <p className="text-white/80">Minutes d'intervention</p>
            </div>
            <div>
              <p className="text-4xl font-bold">24/7</p>
              <p className="text-white/80">Disponibilité urgence</p>
            </div>
          </div>
        </div>

        {/* Values */}
        <div className="mb-16">
          <h2 className="text-2xl md:text-3xl font-bold text-[#262626] mb-8 text-center">
            Nos valeurs
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, idx) => (
              <Card key={idx}>
                <CardContent className="p-6 text-center">
                  <div className="w-14 h-14 bg-[#B91C1C]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <value.icon className="w-7 h-7 text-[#B91C1C]" />
                  </div>
                  <h3 className="font-bold text-[#262626] mb-2">{value.title}</h3>
                  <p className="text-[#666666] text-sm">{value.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Certifications */}
        <div className="bg-[#FAFAFA] rounded-2xl p-8 md:p-12 mb-16">
          <h2 className="text-2xl font-bold text-[#262626] mb-6">Nos engagements</h2>
          <div className="grid md:grid-cols-2 gap-4">
            {[
              "Devis gratuit et transparent avant intervention",
              "Prix fixé à l'avance, sans surprise",
              "Garantie sur toutes nos prestations",
              "Artisans qualifiés et expérimentés",
              "Matériaux de qualité professionnelle",
              "Respect des délais et des rendez-vous",
              "Propreté du chantier après intervention",
              "Service client réactif et à l'écoute"
            ].map((engagement, idx) => (
              <div key={idx} className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-[#B91C1C] flex-shrink-0" />
                <span className="text-[#262626]">{engagement}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Contact Info */}
        <Card>
          <CardContent className="p-8">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <h2 className="text-2xl font-bold text-[#262626] mb-4">Contactez-nous</h2>
                <div className="space-y-4">
                  <a 
                    href={`tel:${companyInfo.phoneEmergency.replace(/\s/g, '')}`}
                    className="flex items-center gap-3 text-[#262626] hover:text-[#B91C1C]"
                  >
                    <div className="w-10 h-10 bg-[#B91C1C]/10 rounded-full flex items-center justify-center">
                      <Phone className="w-5 h-5 text-[#B91C1C]" />
                    </div>
                    <div>
                      <p className="font-semibold">{companyInfo.phoneEmergency}</p>
                      <p className="text-sm text-[#666666]">Urgence 24h/24</p>
                    </div>
                  </a>
                  <a 
                    href={`mailto:${companyInfo.email}`}
                    className="flex items-center gap-3 text-[#262626] hover:text-[#B91C1C]"
                  >
                    <div className="w-10 h-10 bg-[#B91C1C]/10 rounded-full flex items-center justify-center">
                      <Mail className="w-5 h-5 text-[#B91C1C]" />
                    </div>
                    <div>
                      <p className="font-semibold">{companyInfo.email}</p>
                      <p className="text-sm text-[#666666]">Réponse sous 24h</p>
                    </div>
                  </a>
                  <div className="flex items-center gap-3 text-[#262626]">
                    <div className="w-10 h-10 bg-[#B91C1C]/10 rounded-full flex items-center justify-center">
                      <MapPin className="w-5 h-5 text-[#B91C1C]" />
                    </div>
                    <div>
                      <p className="font-semibold">{companyInfo.fullAddress}</p>
                      <p className="text-sm text-[#666666]">Val-de-Marne (94)</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex flex-col gap-3">
                <a href={`tel:${companyInfo.phoneEmergency.replace(/\s/g, '')}`}>
                  <Button size="lg" className="w-full bg-[#B91C1C] hover:bg-[#991B1B]">
                    <Phone className="w-5 h-5 mr-2" />
                    Appeler maintenant
                  </Button>
                </a>
                <Link to="/contact">
                  <Button size="lg" variant="outline" className="w-full border-[#B91C1C] text-[#B91C1C] hover:bg-[#B91C1C] hover:text-white">
                    Demander un devis gratuit
                  </Button>
                </Link>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AboutPage;